import { computed } from '@vue/composition-api';
import type { Route } from 'vue-router';
import { useLaunchDarkly } from '@getaccept/lib-shared/src/launch-darkly/launch-darkly.composable';

import { PathName } from '@getaccept/lib-shared/src/enums/PathNames';
import { UserRoleLegacy } from '@getaccept/lib-shared/src/users/types/user-role-legacy';
import { useFeatureStore } from '@getaccept/feature-restriction';
import { useModulesStore } from '../../modules/store/modules.store';
import { RouterHelper } from '../helpers/router.helper';
import { useSessionStore } from '../../session/store/session.store';
import { useSubscriptionStore } from '../../subscription/store/subscription.store';

export const useRouterHelper = () => {
  const modulesStore = useModulesStore();
  const sessionStore = useSessionStore();
  const subscriptionStore = useSubscriptionStore();
  const { flags } = useLaunchDarkly();
  const featureStore = useFeatureStore();

  const defaultPathName = computed(() =>
    RouterHelper.getDefaultPathNameByModule(modulesStore.activeModule)
  );

  const hasRouteAccess = (to: Route) => {
    const { userRoles, launchDarklyFlags, features, module, custom } = to.meta?.access || {};
    const access: boolean[] = [];

    if (userRoles?.length) {
      access.push(userRoles.includes(sessionStore.session.user_role));
    }

    if (launchDarklyFlags?.length) {
      access.push(launchDarklyFlags.every(flag => !!flags.value[flag]));
    }

    if (features?.length) {
      access.push(!features.some(featureName => featureStore.features[featureName].restricted));
    }

    if (module) {
      access.push(modulesStore.allowedModules.includes(module));
    }

    if (custom) {
      access.push(custom());
    }

    return access.every(Boolean);
  };

  const hasRouteAccessWithInactiveSubscription = (to: Route) => {
    if (!subscriptionStore.isInactiveSubscription) {
      return true;
    }
    const allowedPaths =
      sessionStore.session.user_role === UserRoleLegacy.Admin
        ? [
            PathName.Archive,
            PathName.Contracts,
            PathName.SettingsSecurity,
            PathName.InactiveSubscription,
          ]
        : [PathName.InactiveSubscription];

    return allowedPaths.includes(to.name as PathName);
  };

  const isRedirectingFromMyDocuments = (to: Route) =>
    to.name !== PathName.MyDocumentsRedirect && to.fullPath.includes('my-documents');

  return {
    hasRouteAccess,
    hasRouteAccessWithInactiveSubscription,
    isRedirectingFromMyDocuments,
    defaultPathName,
  };
};
